import { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';

import AccountContext from 'contexts/AccountContext';
import { useChartPalette } from 'contexts/ChartPaletteContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useDateScope from 'hooks/useDateScope';
import useV5ChartData from 'hooks/useV5ChartData';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useValueFormatters from 'hooks/useValueFormatters';
import metricTypeCheckers from 'types/metricTypeCheckers';
import toBarSeries from '../toBarSeries';
import waterfallOptions from '../highchartOptions/waterfallOptions';

// Waterfall reuses the categories x single-series data produced by toBarSeries.
// Only the first series is plotted; each category becomes a delta and a final
// "Total" sum point is appended.
const useWaterfallData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
} => {
  const { unitsLocale, weekStartsOn } = useContext(AccountContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  const { colors } = useChartPalette();
  const { openMenu } = useContext(DashboardContextMenuContext);

  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);
  const [groupField] = useState(getGroupByField(chartDefinition));

  const { formatMetric } = useValueFormatters();
  const dateScope = useDateScope({});
  const { data } = useV5ChartData(chartDefinition);
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'waterfall',
    chartDefinition,
  );

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, data]);

  useEffect(() => {
    if (!data) {
      setIsNoDataToDisplay(false);
      return;
    }

    const { series, xAxis } = toBarSeries(
      data,
      chartDefinition,
      false,
      unitsLocale,
      dateScope,
      11,
      weekStartsOnOverride ? weekStartsOnOverride : weekStartsOn,
      formatMetric,
      undefined,
      colors,
    );

    const firstSeries = series[0];
    const categories = (xAxis?.categories as string[]) ?? [];

    const isNoData =
      !firstSeries ||
      firstSeries.data.length === 0 ||
      !firstSeries.data.some((d) => typeof d === 'number' && !isNaN(d));

    if (isNoData) {
      setIsNoDataToDisplay(true);
      setOptions(undefined);
      return;
    }

    const baseWaterfallOptions = waterfallOptions({
      title: ' ',
      openMenu,
      groupField,
      fieldType: chartDefinition.dimensionA
        ? chartDefinition.dimensionA.fieldType
        : undefined,
      chartDef: chartDefinition,
      showDataLabels: chartDefinition.showDataLabels,
      fontSize: 11,
      formatMetric,
      dateScope,
    });

    const metricIds = metricTypeCheckers.isCompoundMetric(firstSeries.metric)
      ? firstSeries.metric.metricIds
      : [firstSeries.metricId];

    const waterfallSeries = [
      {
        name: firstSeries.name,
        metricId: firstSeries.metricId,
        metricIds,
        type: 'waterfall' as const,
        onDrillDown,
        animation: false,
        data: [...firstSeries.data, { name: 'Total', isSum: true }],
      },
    ];

    setOptions({
      ...baseWaterfallOptions,
      xAxis: {
        categories: [...categories, 'Total'],
        labels: { style: { fontSize: '11px' } },
      },
      series: waterfallSeries,
    } as any as Highcharts.Options);
    setIsNoDataToDisplay(false);
  }, [
    chartDefinition,
    data,
    dateScope,
    formatMetric,
    groupField,
    onDrillDown,
    openMenu,
    unitsLocale,
    colors,
    weekStartsOn,
    weekStartsOnOverride,
  ]);

  return { options, isNoData: isNoDataToDisplay };
};

export default useWaterfallData;
